// Run inventory (Phaser-free; core/run.ts creates one per run). Wraps one
// mutable Loadout with the equip rules from ./loadout and the stat pipeline
// from ./stats. Listeners learn about changes through changed() →
// bus 'inventory:changed' (player refreshes loadout/stats, effects rebuild).

import type { InventoryApi } from '../../core/contracts';
import { bus } from '../../core/events';
import type { Rng } from '../../core/rng';
import type {
  EquipInstance,
  EquipSlot,
  ItemComparison,
  ItemInstance,
  MajorUpgradeId,
  MechDef,
  ModInstance,
  MutationInstance,
  StatBlock,
  SynergyId,
  Tag,
  WeaponInstance,
} from '../../core/types';
import { EQUIP_SLOTS } from '../../core/types';
import { compareItem } from './compare';
import { makeWeapon } from './items';
import {
  applyEquip,
  canAutoEquip,
  normalizeModSlots,
  removeModFrom,
  WEAPON_SLOTS,
  type EquipOptions,
} from './loadout';
import { activeSynergiesFor, computeStatsFor, loadoutItems, tagCountsFor, type Loadout } from './stats';

/** Base scrap per rarity; weapons ×1.3, mods ×0.7. A weapon's installed mods are salvaged with it. */
export const SALVAGE_BASE = { standard: 8, advanced: 18, prototype: 32 } as const;

export function salvageValueOf(item: ItemInstance): number {
  const base = SALVAGE_BASE[item.rarity] ?? SALVAGE_BASE.standard;
  const mult = item.kind === 'weapon' ? 1.3 : item.kind === 'mod' ? 0.7 : 1;
  let value = Math.round(base * mult);
  if (item.kind === 'weapon') for (const m of item.mods) value += salvageValueOf(m);
  return value;
}

/** Concrete inventory: the contract plus a read-only view of the raw loadout. */
export interface Inventory extends InventoryApi {
  /** The mech this inventory was created for (default kit tags / base stats). */
  readonly mech: MechDef;
  /** Raw loadout (treat as read-only; mutate through equip()). */
  readonly loadout: Readonly<Loadout>;
  /** Bumped on every changed() call (cheap cache key for UI). */
  readonly version: number;
}

/** New run inventory: the mech's starting weapon in slot 0, everything else empty. */
export function createInventory(mech: MechDef, rng: Rng): Inventory {
  const emptyEquipment = {} as Record<EquipSlot, EquipInstance | null>;
  for (const s of EQUIP_SLOTS) emptyEquipment[s] = null;

  const l: Loadout = {
    weapons: [makeWeapon(mech.startingWeapon, 'standard', rng), null],
    activeWeapon: 0,
    equipment: emptyEquipment,
    mutations: [],
    upgrades: [],
  };
  let version = 0;
  const kit = (kitTags?: readonly Tag[]): readonly Tag[] => kitTags ?? mech.kitTags;

  const inv: Inventory = {
    mech,
    get loadout() {
      return l;
    },
    get version() {
      return version;
    },
    get weapons(): readonly (WeaponInstance | null)[] {
      return l.weapons;
    },
    get activeWeapon(): number {
      return l.activeWeapon;
    },
    set activeWeapon(i: number) {
      const slot = Math.max(0, Math.min(WEAPON_SLOTS - 1, Math.floor(Number.isFinite(i) ? i : 0)));
      l.activeWeapon = slot;
    },
    get equipment(): Readonly<Record<EquipSlot, EquipInstance | null>> {
      return l.equipment;
    },
    get mutations(): readonly MutationInstance[] {
      return l.mutations;
    },
    get upgrades(): readonly MajorUpgradeId[] {
      return l.upgrades;
    },

    canAutoEquip(item: ItemInstance): boolean {
      return canAutoEquip(l, item);
    },

    equip(item: ItemInstance, opts?: EquipOptions): ItemInstance | null {
      return applyEquip(l, item, opts);
    },

    removeMod(weaponSlot: number, modIndex: number): ModInstance | null {
      return removeModFrom(l, weaponSlot, modIndex);
    },

    addUpgrade(id: MajorUpgradeId): void {
      if (l.upgrades.includes(id)) return;
      l.upgrades.push(id);
      // MOD_SLOT: every weapon gets +1 slot (new weapons are normalised on equip).
      for (const w of l.weapons) if (w) normalizeModSlots(w, l.upgrades);
    },

    salvageValue(item: ItemInstance): number {
      return salvageValueOf(item);
    },

    equippedItems(): ItemInstance[] {
      return loadoutItems(l);
    },

    tagCounts(kitTags?: readonly Tag[]): Map<Tag, number> {
      return tagCountsFor(l, kit(kitTags));
    },

    activeSynergies(kitTags?: readonly Tag[]): SynergyId[] {
      return activeSynergiesFor(l, kit(kitTags));
    },

    computeStats(base: StatBlock, kitTags?: readonly Tag[]): StatBlock {
      return computeStatsFor(l, base, kit(kitTags));
    },

    compare(item: ItemInstance, kitTags?: readonly Tag[]): ItemComparison {
      return compareItem(l, item, mech.base, kit(kitTags));
    },

    visualIds(): string[] {
      const ids: string[] = [];
      for (const s of EQUIP_SLOTS) {
        const e = l.equipment[s];
        if (e) ids.push(e.defId);
      }
      return ids;
    },

    changed(): void {
      version++;
      bus.emit('inventory:changed', {});
    },
  };
  return inv;
}
